import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import { api } from '@/lib/api'
import type { AgentRole, Notification } from '@/types'

// ── Agent Color System ──

const agentColors: Record<AgentRole, { dot: string; text: string; border: string }> = {
  scout:    { dot: 'bg-blue-400',    text: 'text-blue-400',    border: 'border-blue-400' },
  analyst:  { dot: 'bg-violet-400',  text: 'text-violet-400',  border: 'border-violet-400' },
  marketer: { dot: 'bg-emerald-400', text: 'text-emerald-400', border: 'border-emerald-400' },
  closer:   { dot: 'bg-amber-400',   text: 'text-amber-400',   border: 'border-amber-400' },
  dispo:    { dot: 'bg-cyan-400',    text: 'text-cyan-400',    border: 'border-cyan-400' },
  operator: { dot: 'bg-zinc-300',    text: 'text-zinc-300',    border: 'border-zinc-300' },
  sysadmin: { dot: 'bg-slate-400',  text: 'text-slate-400',  border: 'border-slate-400' },
  'billing-ops': { dot: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-400' },
  triage: { dot: 'bg-rose-400',    text: 'text-rose-400',    border: 'border-rose-400' },
}

const agentLabel: Record<AgentRole, string> = {
  scout: 'Scout',
  analyst: 'Analyst',
  marketer: 'Marketer',
  closer: 'Closer',
  dispo: 'Dispo',
  operator: 'Operator',
  sysadmin: 'SysAdmin',
  'billing-ops': 'Billing Ops',
  triage: 'Triage',
}

// ── NotificationPanel ──

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLDivElement | null>
}

export default function NotificationPanel({ open, onClose, anchorRef }: NotificationPanelProps) {
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  const [items, setItems] = useState<Notification[]>([])

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items])

  // Fetch notifications on mount + poll every 30s
  useEffect(() => {
    let mounted = true
    const fetchNotifs = () => {
      api.getNotifications()
        .then((data: any) => {
          if (mounted && data) setItems(Array.isArray(data) ? data : data.notifications || [])
        })
        .catch(() => {})
    }
    fetchNotifs()
    const interval = setInterval(fetchNotifs, 30_000)
    return () => { mounted = false; clearInterval(interval) }
  }, [])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (
        panelRef.current && !panelRef.current.contains(target) &&
        anchorRef.current && !anchorRef.current.contains(target)
      ) {
        onClose()
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose, anchorRef])

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    try {
      await api.markAllNotificationsRead()
    } catch {}
  }, [])

  const handleNotificationClick = useCallback(async (notification: Notification) => {
    setItems((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    )
    try {
      await api.markNotificationRead(notification.id)
    } catch {}
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
    }
    onClose()
  }, [navigate, onClose])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-96 rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-50">Notifications</h3>
          {unreadCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500/20 px-1.5 text-[10px] font-bold text-red-400">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
          >
            <Check className="w-3 h-3" />
            Mark All Read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
            <Bell className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {items.map((notification) => {
              const role = notification.agentRole
              const colors = role ? agentColors[role] : null

              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    'w-full text-left px-4 py-3 transition-all duration-200 hover:bg-zinc-800/70 cursor-pointer',
                    !notification.read && colors
                      ? `border-l-2 ${colors.border} bg-zinc-800/30`
                      : 'border-l-2 border-transparent',
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Agent dot */}
                    <div className="pt-1 shrink-0">
                      <div
                        className={cn(
                          'w-2.5 h-2.5 rounded-full',
                          colors ? colors.dot : 'bg-zinc-500',
                        )}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'text-xs font-semibold uppercase tracking-wide',
                            colors ? colors.text : 'text-zinc-400',
                          )}
                        >
                          {role ? agentLabel[role] : 'System'}
                        </span>
                        <span className="text-[11px] text-zinc-500 shrink-0">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                      <p
                        className={cn(
                          'text-sm mt-0.5 line-clamp-2',
                          notification.read ? 'text-zinc-400' : 'text-zinc-200',
                        )}
                      >
                        {notification.message}
                      </p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-800">
        <button
          onClick={() => {
            navigate('/home')
            onClose()
          }}
          className="w-full py-2.5 text-center text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-colors duration-200 rounded-b-xl"
        >
          View All
        </button>
      </div>
    </div>
  )
}
